import type { NotificationOptions } from 'element-plus'
import type { Router } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { useDocumentVisibility } from '@vueuse/core'
import axios, { AxiosError } from 'axios'
import pkgLodash from 'lodash'
import connectTipCookingUrl_1 from '@/assets/games/cooking/connectInImg-1.webp'
import connectTipCookingUrl_2 from '@/assets/games/cooking/connectInImg-2.webp'
import cooking_gameBannerUrl from '@/assets/games/cooking/gameBanner.webp?url'
import cooking_gameBannerPreviewUrl from '@/assets/games/cooking/gameBannerPreview.webp'
import cooking_gameIconUrl from '@/assets/games/cooking/icon.jpg?url'
import cooking_logoUrl from '@/assets/games/cooking/logo.png?url'
import cooking_coinUrl from '@/assets/games/cooking/loyaltyIcon.webp?url'
import cooking_SectionBg from '@/assets/games/cooking/sectionBg.webp'
import gang_SectionBg from '@/assets/games/gang/bg_gang.webp'
import gang_coinUrl from '@/assets/games/gang/coin.webp?url'
import gang_gameIconUrl from '@/assets/games/gang/gang_icon.jpg?url'
import gang_gameBannerPreviewUrl from '@/assets/games/gang/gangBanner-preview.webp'
import gang_gameBannerUrl from '@/assets/games/gang/gangBanner.webp?url'
import connectTipGangUrl_1 from '@/assets/games/gang/goat_login_tut_gang1.webp'
import connectTipGangUrl_2 from '@/assets/games/gang/goat_login_tut_gang2.webp'
import gang_logoUrl from '@/assets/games/gang/logo_TG.png?url'
import mafia_SectionBg from '@/assets/games/mafia/bg_mafia.webp'
import mafia_coinUrl from '@/assets/games/mafia/coin.webp?url'
import connectTipMafiaUrl_1 from '@/assets/games/mafia/goat_login_tut_mafia1.webp'
import connectTipMafiaUrl_2 from '@/assets/games/mafia/goat_login_tut_mafia2.webp'
import mafia_gameIconUrl from '@/assets/games/mafia/icon.jpg'
import mafia_logoUrl from '@/assets/games/mafia/logo.png'
import mafia_gameBannerPreviewUrl from '@/assets/games/mafia/mafiaBanner-preview.webp'
import mafia_gameBannerUrl from '@/assets/games/mafia/mafiaBanner.webp'
import ql_SectionBg from '@/assets/games/questland/bg_ql.webp'
import ql_coinUrl from '@/assets/games/questland/coin.webp?url'
import connectTipQlUrl_1 from '@/assets/games/questland/goat_login_tut_ql1.webp'
import connectTipQlUrl_2 from '@/assets/games/questland/goat_login_tut_ql2.webp'
import ql_logoUrl from '@/assets/games/questland/logo_QL.png'
import ql_gameIconUrl from '@/assets/games/questland/ql_icon.jpg'
import ql_lgameBannerPreviewUrl from '@/assets/games/questland/qlBanner-preview.webp'
import ql_lgameBannerUrl from '@/assets/games/questland/qlBanner.webp'
import snr_sectionBg from '@/assets/games/slashAndRoll/bg_sn.webp'
import snr_coinUrl from '@/assets/games/slashAndRoll/coin.webp?url'
import connectTipSlashAndRollUrl_1 from '@/assets/games/slashAndRoll/goat_login_tut_slash1.webp'
import connectTipSlashAndRollUrl_2 from '@/assets/games/slashAndRoll/goat_login_tut_slash2.webp'
import snr_logoUrl from '@/assets/games/slashAndRoll/logo_SnR.png?url'
import snr_gameBannerPreviewUrl from '@/assets/games/slashAndRoll/snrBanner-preview.webp'
import snr_gameBannerUrl from '@/assets/games/slashAndRoll/snrBanner.webp'
import snr_gameIconUrl from '@/assets/games/slashAndRoll/sr_icon.jpg?url'
import soccer_SectionBg from '@/assets/games/soccerClash/bg_sc.webp'
import soccer_coinUrl from '@/assets/games/soccerClash/coin.webp?url'
import connectTipSoccerClashlUrl_1 from '@/assets/games/soccerClash/goat_login_tut_soccer_1.webp'
import connectTipSoccerClashlUrl_2 from '@/assets/games/soccerClash/goat_login_tut_soccer_2.webp'
import soccer_logoUrl from '@/assets/games/soccerClash/logo_soccer.png?url'
import soccer_gameBannerPreviewUrl from '@/assets/games/soccerClash/soccerBanner-preview.webp'
import soccer_gameBannerUrl from '@/assets/games/soccerClash/soccerBanner.webp'
import soccer_gameIconUrl from '@/assets/games/soccerClash/socer_icon.jpg?url'
import { spaI18n } from '@/compositions/i18nPlugin'
import { pendingGoatLink } from '@/compositions/securedQueryParams'
import { getLocalePath } from '@/router/langRouteBuilder.ts'
import { useAuthStore } from '@/stores/auth'
import { useGoatStore } from '@/stores/goat'
import { GameKeyEnum, ModeEnum } from '@/types/enums'

const { cloneDeep } = pkgLodash

const isProd = import.meta.env?.VITE_PROD === 'on'

// eslint-disable-next-line unused-imports/no-unused-vars -- kept on purpose (currently unused)
function isVisible(key: string) {
  if (!isProd) {
    return true
  }
  const isSsr = import.meta.env.SSR
  if (!isSsr) {
    return localStorage?.getItem?.(`id-visible_${key}`) === 'on'
  }
  return false
}

const loading = ref('')
const storeHost = isProd ? 'https://store.gamesture.com' : 'https://gamesture-store-dev.vercel.app'

const games: Games.GamesType = {
  gang: {
    name: 'The Gang',
    order: 1,
    logo: gang_logoUrl,
    icon: gang_gameIconUrl,
    gameBanner: gang_gameBannerUrl,
    gameBannerPreview: gang_gameBannerPreviewUrl,
    active: true,
    connectInImg: [connectTipGangUrl_1, connectTipGangUrl_2],
    gameKey: GameKeyEnum.gang,
    imgStorageUrl: 'https://storage.googleapis.com/bucket-gang-files/',
    offerImgId: 886,
    gameRoute: 'game/the-gang',
    sectionBg: gang_SectionBg,
    cardColor: 'transparent',
    loyalty: {
      icon: gang_coinUrl
    },
    itemIconClass: 'max-h-full'
  },
  slashAndRoll: {
    name: 'Slash&Roll',
    order: 2,
    logo: snr_logoUrl,
    icon: snr_gameIconUrl,
    gameBanner: snr_gameBannerUrl,
    gameBannerPreview: snr_gameBannerPreviewUrl,
    active: true,
    connectInImg: [connectTipSlashAndRollUrl_1, connectTipSlashAndRollUrl_2],
    gameKey: GameKeyEnum.slashAndRoll,
    imgStorageUrl: 'https://storage.googleapis.com/bucket-sw-files/',
    offerImgId: 1425,
    gameRoute: 'game/slash-and-roll',
    sectionBg: snr_sectionBg,
    cardColor: 'transparent',
    loyalty: {
      icon: snr_coinUrl
    },
    itemIconClass: 'max-h-full'
  },
  soccerClash: {
    name: 'Soccer Clash',
    order: 3,
    logo: soccer_logoUrl,
    icon: soccer_gameIconUrl,
    gameBanner: soccer_gameBannerUrl,
    gameBannerPreview: soccer_gameBannerPreviewUrl,
    active: true,
    connectInImg: [connectTipSoccerClashlUrl_1, connectTipSoccerClashlUrl_2],
    gameKey: GameKeyEnum.soccerClash,
    imgStorageUrl: 'https://storage.googleapis.com/bucket-soccer-files/',
    offerImgId: 569,
    gameRoute: 'game/soccer-clash',
    sectionBg: soccer_SectionBg,
    cardColor: 'transparent',
    loyalty: {
      icon: soccer_coinUrl
    },
    itemIconClass: 'max-h-full'
  },
  questland: {
    name: 'Questland',
    order: 4,
    logo: ql_logoUrl,
    icon: ql_gameIconUrl,
    gameBanner: ql_lgameBannerUrl,
    gameBannerPreview: ql_lgameBannerPreviewUrl,
    active: true,
    connectInImg: [connectTipQlUrl_1, connectTipQlUrl_2],
    gameKey: GameKeyEnum.questland,
    imgStorageUrl: 'https://storage.googleapis.com/ql4-files-eu/',
    offerImgId: 26055,
    gameRoute: 'game/questland',
    sectionBg: ql_SectionBg,
    cardColor: 'transparent',
    loyalty: {
      icon: ql_coinUrl
    },
    itemIconClass: '-translate-y-[20%]'
  },
  mafia: {
    name: 'Mafia Rivals',
    order: 5,
    logo: mafia_logoUrl,
    icon: mafia_gameIconUrl,
    gameBanner: mafia_gameBannerUrl,
    gameBannerPreview: mafia_gameBannerPreviewUrl,
    active: true,
    connectInImg: [connectTipMafiaUrl_1, connectTipMafiaUrl_2],
    gameKey: GameKeyEnum.mafia,
    imgStorageUrl: 'https://storage.googleapis.com/bucket-mafia-files/',
    offerImgId: 568,
    gameRoute: 'game/mafia',
    sectionBg: mafia_SectionBg,
    cardColor: 'transparent',
    loyalty: {
      icon: mafia_coinUrl
    },
    itemIconClass: 'max-h-full'
  },
  cooking: {
    name: 'Cooking Wars',
    order: 6,
    logo: cooking_logoUrl,
    icon: cooking_gameIconUrl,
    gameBanner: cooking_gameBannerUrl,
    gameBannerPreview: cooking_gameBannerPreviewUrl,
    active: true,
    connectInImg: [connectTipCookingUrl_1, connectTipCookingUrl_2],
    gameKey: GameKeyEnum.cooking,
    imgStorageUrl: 'https://storage.googleapis.com/bucket-cooking-files/',
    offerImgId: 1361,
    gameRoute: 'game/cooking-wars',
    sectionBg: cooking_SectionBg,
    cardColor: 'transparent',
    loyalty: {
      icon: cooking_coinUrl
    },
    itemIconClass: 'max-h-full'
  }
}

const gameOrder = Object.entries(games)
  .sort((a, b) => a[1].order - b[1].order)
  .map(([gameKey]) => gameKey as Games.GameKeyType)
  .filter(gameKey => games?.[gameKey]?.active)

function showError(elPlusErrorConfig: Partial<NotificationOptions> | null = null) {
  const defaultConfig = {
    title: spaI18n.global.t('common.error'),
    type: 'warning',
    customClass: 'child-inherit-colors text-red-600 z-999999',
    message: spaI18n.global.t('common.somethingWentWrong'),
    duration: 9000
  }
  appUtilsPlugin.getAppGlobalProperties()?.$notify({ ...defaultConfig, ...elPlusErrorConfig })
}

function catchError(error: Error | boolean | AxiosError) {
  if (error instanceof AxiosError) {
    appUtilsPlugin.getAppGlobalProperties()?.$notify({
      title: spaI18n.global.t('common.error'),
      type: 'warning',
      customClass: 'child-inherit-colors text-red-600 z-999999',
      message: `${error?.response?.data?.message || error?.response?.data?.errors?.[0] || error}`,
      duration: 9000
    })
  } else if (error instanceof Error) {
    console?.error?.(error)
  }
}

const triggerRef = ref(0)
const isVisibleDocument = useDocumentVisibility()
const triggerApi = {
  triggerRef,
  callTrigger: (delay = 0) => {
    if (delay) {
      setTimeout(() => {
        triggerRef.value = ++triggerRef.value % 10
      }, delay)
    } else {
      triggerRef.value = ++triggerRef.value % 10
    }
  },
  initTriggerApi: () => {
    const isSsr = import.meta.env.SSR
    if (!isSsr) {
      checkBuildVersion(true)
      const timeout = localStorage?.getItem?.('devtimeout') ? Number.parseInt(String(localStorage?.getItem?.('devtimeout'))) : 60
      if (timeout !== 60) {
        // eslint-disable-next-line no-alert
        alert?.('devtimeout ENABLED !!!!!!!!!!')
      }
      const intervalID = setInterval(() => {
        if (isVisibleDocument.value !== 'hidden') {
          checkBuildVersion()
          triggerApi.callTrigger()
        }
      }, timeout * 1000)

      onUnmounted(() => {
        if (intervalID) {
          clearInterval(intervalID)
        }
      })
    }
  }
}

function checkBuildVersion(init = false) {
  const isSsr = import.meta.env.SSR
  const isDev = import.meta.env.DEV
  const host = import.meta.env.VITE_HOST || ''
  if (!isSsr && !isDev) {
    let deployVersion = localStorage?.getItem?.('deploy-version')
    if (deployVersion && deployVersion?.length > 50) {
      deployVersion = ''
      localStorage.setItem?.('deploy-version', deployVersion)
    }

    const axiosInst = axios.create()
    axiosInst.get(`${host}/build.txt`)
      .then((data) => {
        const buildTxt = data?.data
        if (buildTxt && buildTxt?.length < 50) {
          if (!init) {
            if (buildTxt && deployVersion && buildTxt !== deployVersion) {
              appUtilsPlugin.getAppGlobalProperties()?.$notify({
                title: spaI18n.global.t('newVersion.title'),
                type: 'warning',
                customClass: 'child-inherit-colors text-orange-400 z-999999',
                dangerouslyUseHTMLString: true,
                message: `<div class="text-left">${spaI18n.global.t('newVersion.messageBeforeLink')} <a class="text-gs-main-color underline cursor-pointer hover:text-orange-600 active:text-orange-500" onclick="location?.reload?.()" >${spaI18n.global.t('newVersion.refreshStore')}</a>.</div>`,
                duration: 58000
              })
            }
          } else {
            localStorage?.setItem?.('deploy-version', buildTxt)
          }
        } else {
          if (Sentry?.getClient()) {
            Sentry.captureException(new Error('not valid buildTxt'), {
              extra: {
                init,
                buildTxt: (buildTxt?.length ? buildTxt?.substring(0, 50) : buildTxt) || '-',
                axios: true,
                msg: 'not valid buildTxt in checkBuildVersion (v1)'
              }
            })
          }
        }
      })
      .catch((error) => {
        if (Sentry?.getClient()) {
          Sentry.captureException(error, {
            extra: {
              init,
              errorData: {
                response: {
                  status: error?.response?.status,
                  data: error?.response?.data ? error?.response?.data?.substring(0, 50) : error?.response?.data
                },
                config: {
                  url: error?.config?.url
                },
                code: error?.code,
                message: error?.message
              },
              axios: true,
              msg: 'catch error in checkBuildVersion (v1)'
            }
          })
        }
      })
  }
}

const mode = ref<ModeEnum>(ModeEnum.login)
function updateUlrMode(nameMode: ModeEnum, router: Router) {
  if (ModeEnum.unknown !== nameMode) {
    let hash = ''
    if (![ModeEnum.loggedin, ModeEnum.unknown].includes(nameMode)) {
      hash = `#${nameMode}`
    }
    // keep the current query (UTM / campaign params, flags) - only the screen (#hash) changes; auth params never get here
    // (the router guard takes them out of the URL first)
    router.push({ path: getLocalePath('home-page'), query: router.currentRoute.value.query, hash })
  }
}
const unsubscribeToken = ref('')

function updateMode(calledMode: ModeEnum) {
  const auth = useAuthStore()
  const app = appUtilsPlugin.getAppGlobalProperties()
  const route = app?.$route
  const router = app?.$router
  if (route && router) {
    if (pendingGoatLink.value && calledMode === ModeEnum.unknown) {
      // goat manage account link from store - params already taken out of the URL by the router guard; consumed only by the
      // home page's initial updateMode(unknown), never by an explicit action (register / login buttons...)
      // (cleared before login: auth.login() calls updateMode(loggedin) itself, which sets the mode)
      const { token, email, app } = pendingGoatLink.value
      pendingGoatLink.value = null
      auth.login(token, email)
      if (auth.isLogged && auth.getToken === token) { // an undecodable token keeps a previous session - no false login event
        const { gaLogin, gaLoginMethods, gaSections } = gaUtils
        // GA is usually still loading here (stored consent) - an event sent now would be dropped
        gaUtils.whenGaReady(() => {
          gaLogin({ method: gaLoginMethods.goat, gs_section: app ? `${gaSections?.app}_${app}` : gaSections.storePage })
        })
      }
      return
    }
    const goat = useGoatStore()
    const hashRoute = (route.hash || '').slice(1)
    if (calledMode === ModeEnum.unknown) {
      if (unsubscribeToken.value) {
        mode.value = ModeEnum.unsubscribe
      } else if (ModeEnum.login === hashRoute) {
        mode.value = ModeEnum.login
      } else if (ModeEnum.register === hashRoute) {
        mode.value = ModeEnum.register
      } else if (ModeEnum.resetPassword === hashRoute) {
        if (goat.getGoatDetails?.serviceToken?.length) {
          mode.value = ModeEnum.resetPassword
        } else {
          mode.value = ModeEnum.login
          updateUlrMode(ModeEnum.login, router)
        }
      } else if (auth.isLogged) {
        mode.value = ModeEnum.loggedin
      } else {
        goat.clearGoatData()
        mode.value = ModeEnum.login
      }
    } else if ([ModeEnum.acceptTerms, ModeEnum.forgotPassword, ModeEnum.resetPassword, ModeEnum.confirmEmail].includes(calledMode)) {
      mode.value = calledMode
    } else if ((calledMode === ModeEnum.loggedin && auth.isLogged) || calledMode === ModeEnum.register) {
      mode.value = calledMode
      updateUlrMode(calledMode, router)
    } else {
      goat.clearGoatData()
      mode.value = ModeEnum.login
      updateUlrMode(ModeEnum.login, router)
    }
  }
}

function getGoatApiUrl(endpoint: string) {
  const apiHost = import.meta.env.VITE_GOAT_API
  return `${apiHost}/api/${endpoint}/`
}
function numbersShortHuman(number: number) {
  if (number >= 10_000) {
    if (number >= 1000_000_000) {
      return `${Number.parseFloat((Math.round((number / 1000_000_000) * 100) / 100).toFixed(2))}G`
    } else if (number >= 1000_000) {
      return `${Number.parseFloat((Math.round((number / 1000_000) * 100) / 100).toFixed(2))}M`
    } else {
      return `${Number.parseFloat((Math.round((number / 1_000) * 100) / 100).toFixed(2))}k`
    }
  }
  return `${number}`
}

const resetPasswordEmail = ref('')

const siteUtils = {
  games,
  gameOrder,
  isProd,
  catchError,
  showError,
  triggerApi,
  isVisibleDocument,
  loading,
  mode,
  updateMode,
  getGoatApiUrl,
  cloneDeep,
  storeHost,
  resetPasswordEmail,
  unsubscribeToken,
  numbersShortHuman
}

export { siteUtils }
